"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { X } from "lucide-react";

import type { StoreSizeGuide } from "@/components/store/types";

type SizeGuideModalProps = Readonly<{
  isOpen: boolean;
  sizeGuide: StoreSizeGuide;
  onClose: () => void;
}>;

export const SizeGuideModal = ({
  isOpen,
  sizeGuide,
  onClose,
}: SizeGuideModalProps): React.ReactElement => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) {
      return;
    }

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent p-3 backdrop:bg-black/40 backdrop:backdrop-blur-xs sm:p-4"
      aria-labelledby="size-guide-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={() => {
        if (isOpen) {
          onClose();
        }
      }}
    >
      <div
        className="flex h-full w-full items-center justify-center"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="border-border relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border bg-white shadow-2xl md:rounded-3xl">
          <div className="border-border flex items-center justify-between border-b px-4 py-5 sm:px-6">
            <h2
              id="size-guide-title"
              className="font-sora text-foreground text-xl font-semibold"
            >
              Size guide
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-foreground hover:text-primary hover:bg-primary/5 flex h-11 w-11 items-center justify-center rounded-full transition-colors"
              aria-label="Close size guide"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
            {sizeGuide.imageUrl && (
              <div className="bg-primary/5 relative mb-6 aspect-square w-full overflow-hidden rounded-2xl">
                <Image
                  src={sizeGuide.imageUrl}
                  alt="Product measurement diagram"
                  fill
                  sizes="(max-width: 512px) 100vw, 512px"
                  className="object-contain"
                />
              </div>
            )}

            {sizeGuide.notes.length > 0 && (
              <div className="text-secondary space-y-2 text-sm leading-6">
                {sizeGuide.notes.map((note, index) => (
                  <p key={index}>{note}</p>
                ))}
              </div>
            )}

            {sizeGuide.measurements.length > 0 && (
              <dl className="mt-6 space-y-4">
                {sizeGuide.measurements.map((measurement) => (
                  <div key={measurement.label}>
                    <dt className="font-sora text-foreground text-sm font-semibold">
                      {measurement.label}
                    </dt>
                    <dd className="text-secondary mt-1 text-sm leading-6">
                      {measurement.description}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
};
